import React, {type ReactNode, useEffect, useState} from 'react';

import cs from 'classnames';
import get from 'lodash/get';
import {useSelector} from 'react-redux';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {CurrentUserSelector} from 'app/current-user/api';
import {
  signoffRequest,
  type SignoffRequestPayloadType,
} from 'app/general/actions';
import {getUserLink} from 'app/general/dashboards/ItemLastActivity';
import {createFormErrorsContext, type FormErrorsProps} from 'app/general/forms';
import {BaseActionableDocumentType} from 'app/general/types';
import StateButton from 'app/general/widgets/StateButton';
import {DocumentIcon} from 'app/notifly/components/DocumentIconWithNotifications';
import {requestSignoffTemplateList} from 'app/signoffs/actions';
import {sweetAPI} from 'app/store/sweet-api';
import {useNotifications} from 'app/toasts/hooks';
import {
  getPoliciesUrlForTeam,
  getProceduresUrlForTeam,
  getProcessesUrlForTeam,
  getSignoffUrlForTeam,
} from 'app/urls';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {SIGNOFF_TAGS} from 'app/utils/rtk-tags';

import type {
  BasicTeamSerializedType,
  BasicUserSerializedType,
  SignoffTemplateSerializedType,
  TeamMembershipSerializedType,
} from 'sweet-types';

type TeamBadgeListProps = {
  text: string;
  teams: Array<BasicTeamSerializedType>;
  extraTeams?: Array<BasicTeamSerializedType>;
  urlExtractor?: (team: BasicTeamSerializedType) => string;
};

const MAX_TEAM_DISPLAY = 10;

function TeamBadgeList({
  extraTeams = [],
  text,
  teams,
  urlExtractor,
}: TeamBadgeListProps) {
  if (teams.length === 0) {
    return null;
  }

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  return (
    <div className="list-group my-3">
      <div className="small text-secondary mt-2">{text}</div>
      {teams.map((basicTeam) => (
        <TeamBadge
          key={basicTeam.id}
          basicTeam={basicTeam}
          url={
            urlExtractor != null ? urlExtractor(basicTeam) : basicTeam.html_url
          }
        />
      ))}
      {extraTeams.length > 0 && isCollapsed ? (
        <a
          href="#"
          className="list-group-item list-group-item-action text-primary"
          onClick={() => setIsCollapsed(false)}
        >
          {gt('And %s more...', extraTeams.length)}
        </a>
      ) : null}
      {!isCollapsed
        ? extraTeams.map((basicTeam) => (
            <TeamBadge
              key={basicTeam.id}
              basicTeam={basicTeam}
              url={
                urlExtractor != null
                  ? urlExtractor(basicTeam)
                  : basicTeam.html_url
              }
            />
          ))
        : null}
    </div>
  );
}

type TeamBadgeProps = {
  basicTeam: BasicTeamSerializedType;
  url: string;
};

function TeamBadge({basicTeam, url}: TeamBadgeProps) {
  return (
    <a href={url} className="list-group-item list-group-item-action">
      <span>
        <i className="fa fa-fw fa fa-users mr-1"></i>
        {basicTeam.name}
      </span>
    </a>
  );
}

function excludeTeams(
  teams: Array<BasicTeamSerializedType>,
  toExclude: Array<BasicTeamSerializedType>
) {
  return teams.filter(
    (team) => !toExclude.find((excludedTeam) => excludedTeam?.url === team.url)
  );
}

function teamMembershipsToBasicTeams(
  document: BaseActionableDocumentType,
  allBasicTeamsMapper: Record<string, BasicTeamSerializedType>
): Array<BasicTeamSerializedType> {
  let teamMemberships: Array<TeamMembershipSerializedType>;

  if (document.content_type === 'document') {
    teamMemberships = document.document.team_memberships;
  } else {
    teamMemberships = document.team_memberships;
  }

  const documentTeams: Array<BasicTeamSerializedType> = teamMemberships
    .filter((teamMembership) => allBasicTeamsMapper[teamMembership.team])
    .map((teamMembership) => {
      return allBasicTeamsMapper[teamMembership.team];
    });
  return documentTeams;
}

type OwnProps = {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  document: BaseActionableDocumentType;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (doc?: BaseActionableDocumentType) => unknown;
};
type Props = OwnProps & FormErrorsProps;

function BaseSignoffRequestModal({
  document,
  contentType,
  onClose,
  isOpen,
  onSuccess,
  captureFormErrorsFromRejection,
}: Props) {
  const ct = getContentType(contentType);
  const [status, setStatus] = useState<'start' | 'success' | 'error'>('start');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const {showToastNotification} = useNotifications();
  const signoffRequestedAt: null | string =
    document.content_type === 'document'
      ? document.document.signoff_requested_at
      : document.signoff_requested_at;
  const signoffRequestedBy: null | BasicUserSerializedType =
    document.content_type === 'document'
      ? document.document.signoff_requested_by
      : document.signoff_requested_by;
  const [teamsWithSignoffs, setMembershipsWithSignoffs] = useState<
    Array<BasicTeamSerializedType>
  >([]);
  const [extraTeamsWithSignoffs, setExtraMembershipsWithSignoffs] = useState<
    Array<BasicTeamSerializedType>
  >([]);
  const [fetchingSignoffTemplates, setFetchingSignoffTemplates] =
    useState<boolean>(true);
  const allBasicTeamsMapper: Record<string, BasicTeamSerializedType> =
    useSelector((state) => {
      const teams = state.teamsArea.get('basicTeams');
      const teamUrlMapper: Record<string, BasicTeamSerializedType> = {};

      if (teams == null) {
        return teamUrlMapper;
      }

      teams.forEach((basicTeam) => {
        const team = basicTeam.toJS();
        teamUrlMapper[team.url] = team;
        return true;
      });
      return teamUrlMapper;
    });
  const userTeams: Array<BasicTeamSerializedType> = useSelector((state) => {
    return CurrentUserSelector(state).data!.teams;
  });
  const documentTeams: Array<BasicTeamSerializedType> =
    teamMembershipsToBasicTeams(document, allBasicTeamsMapper);
  const actions = useAsyncActions({
    signoffRequest,
    invalidateTags: sweetAPI.util.invalidateTags,
  });
  // 1. get signoff templates
  useEffect(() => {
    requestSignoffTemplateList({
      page: '1',
      page_size: UNLIMITED_PAGE_SIZE,
      ordering: 'team__name',
    }).then((data) => {
      const signoffTemplates: Array<SignoffTemplateSerializedType> =
        data.results;
      const memberOfTeams: Array<BasicTeamSerializedType> = [];
      const extraMemberOfTeams: Array<BasicTeamSerializedType> = [];
      let count = 0;
      signoffTemplates.map((signoffTemplate) => {
        if (signoffTemplate.is_trigger_on_signoff_requested) {
          documentTeams.map((basicTeam) => {
            if (signoffTemplate.team === basicTeam.url) {
              if (count < MAX_TEAM_DISPLAY) {
                memberOfTeams.push(basicTeam);
              } else {
                extraMemberOfTeams.push(basicTeam);
              }

              count++;
            }
          });
        }
      });
      setMembershipsWithSignoffs(memberOfTeams);
      setExtraMembershipsWithSignoffs(extraMemberOfTeams);
      setFetchingSignoffTemplates(false);
    });
  }, []);

  const generateSignoff = (e: React.MouseEvent) => {
    e.preventDefault();
    if (teamsWithSignoffs.length === 0 || document.can_edit == false) {
      return;
    }
    const payload: SignoffRequestPayloadType = {
      contentType,
      current_version:
        document.content_type === 'document'
          ? document.document.current_version
          : document.current_version,
    };
    return actions.signoffRequest(payload).then(
      () => {
        onSuccess(document);
        onClose();
        showToastNotification(gt('Sign Off request is being created.'), {
          level: 'success',
        });

        // We need to invalidate signoff cache whenever signoff is requested.
        // We will remove this once we rtkfy this component.
        actions.invalidateTags([
          // @ts-expect-error we inject tags on the API after it's created so TS doesn't know bout them
          {type: SIGNOFF_TAGS.TAGS, id: 'list'},
        ]);
      },
      (err) => {
        if (get(err, 'response.status') === 400) {
          setStatus('start');
          captureFormErrorsFromRejection(err);
        } else {
          setStatus('error');
        }

        return err;
      }
    );
  };

  const disabledSignoffsTeams = excludeTeams(
    documentTeams,
    teamsWithSignoffs.concat(extraTeamsWithSignoffs)
  );
  const docNotAttachedTeams = excludeTeams(userTeams, documentTeams);
  return (
    <TabbedModal
      show={isOpen}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Require Sign Off for this version'),
          icon: ct.icon,
          content: (
            <>
              {status === 'start' ? (
                <div className="mb-2">
                  {fetchingSignoffTemplates ? null : teamsWithSignoffs.length ===
                    0 ? (
                    <MainStatusCard
                      color="warning"
                      iconClasses="fa-exclamation-triangle "
                      mainMessage={
                        documentTeams.length > 0
                          ? '%(document)s is not attached to any team with Sign Offs'
                          : '%(document)s is not attached to any team'
                      }
                      document={document}
                      additionalContent={
                        <div className="small text-secondary mt-2">
                          {gt('To request Sign Off for this document:')}
                          <ul>
                            <li>
                              {gt(
                                'Add it to a team that has Sign Off rules configured'
                              )}{' '}
                            </li>
                            <li>
                              {gt(
                                'Enable "When managers choose" in team\'s Sign Off rules'
                              )}
                            </li>
                          </ul>
                        </div>
                      }
                    />
                  ) : signoffRequestedAt != null &&
                    signoffRequestedBy != null ? (
                    <MainStatusCard
                      color="success"
                      iconClasses="fa-check-circle"
                      mainMessage="Sign Off required already for this version of %(document)s"
                      document={document}
                      additionalContent={
                        <ElapsedTime
                          timestamp={signoffRequestedAt}
                          ContainerTag="small"
                          className="d-block mt-2 text-secondary"
                          wrapTextFn={(timeago) =>
                            gt('%(timeago)s by %(user)s', {
                              timeago,
                              user: getUserLink(signoffRequestedBy),
                            })
                          }
                        />
                      }
                    />
                  ) : (
                    <MainStatusCard
                      color="info"
                      iconClasses="fa-check-circle"
                      mainMessage="No Sign Off required yet for this version of %(document)s"
                      document={document}
                    />
                  )}

                  <div>
                    {fetchingSignoffTemplates === false ? (
                      <div>
                        <TeamBadgeList
                          text={ngt(
                            'Members of this team will be required to sign off this document.',
                            'Members of these teams will be required to sign off this document.',
                            teamsWithSignoffs.length
                          )}
                          teams={teamsWithSignoffs}
                          extraTeams={extraTeamsWithSignoffs}
                        />

                        {docNotAttachedTeams.length > 0 ||
                        disabledSignoffsTeams.length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => {
                              setIsCollapsed(!isCollapsed);
                            }}
                          >
                            <small>
                              {isCollapsed
                                ? gt('Show other teams')
                                : gt('Collapse')}{' '}
                              <i
                                className={`fa fa-chevron-${
                                  isCollapsed ? 'down' : 'up'
                                }`}
                              />
                            </small>
                          </button>
                        ) : null}

                        {isCollapsed ? null : (
                          <>
                            <TeamBadgeList
                              text={gt(
                                'The "When managers choose" Sign Off rule is not enabled for these teams'
                              )}
                              teams={disabledSignoffsTeams}
                              urlExtractor={(team: BasicTeamSerializedType) =>
                                getSignoffUrlForTeam(team)
                              }
                            />
                            <TeamBadgeList
                              text={gt(
                                'This document is not attached to these teams.'
                              )}
                              urlExtractor={(team: BasicTeamSerializedType) =>
                                contentType === 'procedure'
                                  ? getProceduresUrlForTeam(team)
                                  : contentType === 'proceduregroup'
                                  ? getProcessesUrlForTeam(team)
                                  : contentType === 'policy'
                                  ? getPoliciesUrlForTeam(team)
                                  : team.html_url
                              }
                              teams={docNotAttachedTeams}
                            />
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : status === 'error' ? (
                <div className="alert alert-danger my-1">
                  {gt('Request failed. Sign Offs were not requested.')}
                </div>
              ) : null}

              <StyledModalFooter>
                <button
                  onClick={onClose}
                  type="button"
                  className="btn btn-link text-secondary"
                >
                  <i className="fa fa-times mr-1" />
                  {status === 'start' ? gt('Cancel') : gt('Close')}
                </button>
                {status === 'start' && (
                  <LazyTooltip
                    hide={teamsWithSignoffs.length > 0}
                    tooltip={gt(
                      'At least one team must have Sign Offs enabled.'
                    )}
                  >
                    <StateButton
                      className={cs('btn btn-primary', {
                        disabled:
                          !document.can_edit || teamsWithSignoffs.length === 0,
                      })}
                      onClick={generateSignoff}
                      busyText={gt('Requesting Sign Off...')}
                    >
                      {gt('Request Sign Off')}
                    </StateButton>
                  </LazyTooltip>
                )}
              </StyledModalFooter>
            </>
          ),
        },
      ]}
    />
  );
}

export const SignoffRequestModal = createFormErrorsContext(
  BaseSignoffRequestModal
);

type MainStatusCardProps = {
  color: 'success' | 'warning' | 'info';
  iconClasses: string;
  mainMessage: string;
  document: BaseActionableDocumentType;
  additionalContent?: ReactNode;
};

function MainStatusCard({
  color,
  iconClasses,
  mainMessage,
  document,
  additionalContent,
}: MainStatusCardProps) {
  return (
    <div className={cs('px-0 pt-2 pb-3 border-bottom mb-2')}>
      <div className="d-flex">
        <h4
          className={cs(
            'fa fa-fw mr-2 mt-1',
            color ? `text-${color}` : null,
            iconClasses
          )}
        />
        <div>
          <div></div>
          {gt(mainMessage, {
            document: (
              <div className="font-weight-bold d-block">
                <DocumentIcon
                  className="mr-1"
                  contentType={document.content_type}
                />
                {getObjectName(document, document.content_type, true)}
              </div>
            ),
          })}
          {additionalContent != null ? <div>{additionalContent}</div> : null}
        </div>
      </div>
    </div>
  );
}
